import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api.js';
import { useAuth } from './AuthContext.jsx';
import { useFamily } from './FamilyContext.jsx';

const QuestionnaireDataContext = createContext(null);

export function QuestionnaireDataProvider({ children }) {
  const { token, user } = useAuth();
  const { family, members } = useFamily();

  // Signature of the profile fields that gate question/category visibility.
  // When it changes (profile/foyer edited), the visible question set must be refetched.
  const memberGateSignature = useMemo(() => {
    const m = (members || []).find((member) => member.uuid === user?.uuid);
    if (!m) return '';
    return [
      m.housing_type,
      m.has_pets,
      m.has_livestock,
      m.has_vehicle,
      m.cares_for_dependent,
      m.children_full_time_count,
      m.children_shared_custody_count
    ].join('|');
  }, [members, user?.uuid]);

  const [questions, setQuestions] = useState(null);
  const [answers, setAnswersState] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const questionsRef = useRef(null);
  const answersRef = useRef(null);

  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  const canLoadQuestionnaire = Boolean(token && family);

  const setAnswers = useCallback((nextAnswers) => {
    setAnswersState((prev) => {
      if (typeof nextAnswers === 'function') {
        return nextAnswers(prev || {});
      }
      return nextAnswers || {};
    });
  }, []);

  const ensureQuestionnaireData = useCallback(async ({
    forceQuestions = false,
    forceAnswers = false,
    keepExisting = true
  } = {}) => {
    if (!canLoadQuestionnaire) {
      requestIdRef.current += 1;
      setQuestions(null);
      setAnswersState(null);
      setStatus('idle');
      setError('');
      return { questions: null, answers: null };
    }

    const currentQuestions = questionsRef.current;
    const currentAnswers = answersRef.current;
    const needsQuestions = forceQuestions || !currentQuestions;
    const needsAnswers = forceAnswers || !currentAnswers;

    if (!needsQuestions && !needsAnswers) {
      return { questions: currentQuestions, answers: currentAnswers };
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const hasExistingData = Boolean((currentQuestions && keepExisting) || (currentAnswers && keepExisting));
    setStatus(hasExistingData ? 'refreshing' : 'loading');
    setError('');

    try {
      const [questionsPayload, answersPayload] = await Promise.all([
        needsQuestions ? apiFetch('/api/questions', { token }) : Promise.resolve(null),
        needsAnswers ? apiFetch('/api/answers/mine', { token }) : Promise.resolve(null)
      ]);

      if (requestIdRef.current !== requestId) {
        return {
          questions: questionsPayload || currentQuestions,
          answers: answersPayload?.answers || currentAnswers
        };
      }

      if (questionsPayload) {
        setQuestions(questionsPayload);
      }
      if (answersPayload) {
        setAnswersState(answersPayload.answers || {});
      }

      setStatus('ready');
      return {
        questions: questionsPayload || currentQuestions,
        answers: answersPayload?.answers || currentAnswers
      };
    } catch (err) {
      if (requestIdRef.current !== requestId) {
        throw err;
      }

      setError(err?.message || 'Impossible de charger le questionnaire.');
      setStatus((currentQuestions || currentAnswers) && keepExisting ? 'ready' : 'error');
      throw err;
    }
  }, [canLoadQuestionnaire, token]);

  const refreshAnswers = useCallback(() => (
    ensureQuestionnaireData({ forceAnswers: true, keepExisting: true })
  ), [ensureQuestionnaireData]);

  const refreshQuestionnaireData = useCallback(() => (
    ensureQuestionnaireData({ forceQuestions: true, forceAnswers: true, keepExisting: true })
  ), [ensureQuestionnaireData]);

  useEffect(() => {
    if (!canLoadQuestionnaire) {
      requestIdRef.current += 1;
      setQuestions(null);
      setAnswersState(null);
      setStatus('idle');
      setError('');
    }
  }, [canLoadQuestionnaire]);

  // Auto-refetch the visible questions when the gating profile changes (no manual reload needed).
  const loadedGateSigRef = useRef(null);
  useEffect(() => {
    if (!canLoadQuestionnaire) {
      loadedGateSigRef.current = null;
      return;
    }
    if (
      questionsRef.current &&
      loadedGateSigRef.current !== null &&
      loadedGateSigRef.current !== memberGateSignature
    ) {
      ensureQuestionnaireData({ forceQuestions: true, keepExisting: true }).catch(() => {});
    }
    loadedGateSigRef.current = memberGateSignature;
  }, [memberGateSignature, canLoadQuestionnaire, ensureQuestionnaireData]);

  const value = useMemo(() => ({
    answers,
    ensureQuestionnaireData,
    error,
    isLoading: status === 'loading' || status === 'refreshing',
    questions,
    refreshAnswers,
    refreshQuestionnaireData,
    setAnswers,
    status
  }), [answers, ensureQuestionnaireData, error, questions, refreshAnswers, refreshQuestionnaireData, setAnswers, status]);

  return (
    <QuestionnaireDataContext.Provider value={value}>
      {children}
    </QuestionnaireDataContext.Provider>
  );
}

export function useQuestionnaireData() {
  const context = useContext(QuestionnaireDataContext);

  if (!context) {
    throw new Error('useQuestionnaireData must be used within QuestionnaireDataProvider');
  }

  return context;
}
